/**
 * FluxNode API Service
 *
 * Service layer for interacting with the Flux blockchain API
 * to fetch node information, rewards, network statistics, and parallel assets.
 *
 * Based on 2ndtlmining/Fluxnode implementation:
 * https://github.com/2ndtlmining/Fluxnode
 */

import { callCoinGeckoAPI } from './api';
import type {
  FluxNode,
  NodeListResponse,
  NetworkStats,
  BenchmarkData,
  ParallelChain
} from '../types/fluxNode.types';

// Parallel Assets summary from Fusion API
export interface ParallelAssetsSummary {
  address: string;
  totalClaimable: number;
  totalClaimed: number;
  totalReceived: number;
  fusionFees: number;
  assets: ParallelAssetDetail[];
}

export interface ParallelAssetDetail {
  chain: ParallelChain;
  symbol: string;
  claimable: number;
  claimed: number;
  received: number;
  pending: number;
}

// Reward payout from Explorer API
export interface RewardPayout {
  txid: string;
  timestamp: number;
  amount: number; // in FLUX
  blockHeight: number;
}

// Deployed App specification from Flux Apps API
export interface FluxDeployedApp {
  name: string;
  description: string;
  owner: string;
  hash: string;
  height: number; // Block height when deployed
  version: number;
  instances: number;
  compose: FluxAppComponent[];
  contacts: string[];
  geolocation: string[];
  expire: number; // Block height when app expires
  nodes: string[]; // IP addresses of nodes running the app
  staticip?: boolean;
}

export interface FluxAppComponent {
  name: string;
  description: string;
  repotag: string; // Docker image
  owner: string;
  ports: number[];
  domains: string[];
  environmentParameters: string[];
  commands: string[];
  containerPorts: number[];
  containerData: string;
  cpu: number;
  ram: number;
  hdd: number;
  tiered: boolean;
  secrets?: string;
  repoauth?: string;
}

// App instance running on a specific node
export interface FluxAppInstance {
  name: string;
  ip: string;
  broadcastedAt: string;
  expireAt: string;
  hash: string;
}

// Summary of deployed apps for a wallet
export interface FluxDeployedAppsSummary {
  owner: string;
  totalApps: number;
  totalInstances: number;
  apps: FluxDeployedApp[];
  totalCpu: number;
  totalRam: number;
  totalHdd: number;
}

// Node count response from daemon API
export interface NodeCountResponse {
  status: string;
  data: {
    'cumulus-enabled': number;
    'nimbus-enabled': number;
    'stratus-enabled': number;
    total: number;
  };
}

// Extended network stats with APY
export interface ExtendedNetworkStats extends NetworkStats {
  cumulusAPY: number;
  nimbusAPY: number;
  stratusAPY: number;
  blocksPerDay: number;
  rewardPerBlock: number;
}

class FluxNodeAPIService {
  // Use Vercel serverless function to avoid CORS issues
  // DEV: falls back to direct API calls (will have CORS issues without proxy)
  // PROD: uses Vercel function at /api/flux
  private useProxy = typeof window !== 'undefined' && !window.location.hostname.includes('localhost');
  private proxyUrl = '/api/flux'; // Vercel serverless function endpoint

  // PoUW v2 / PoN constants
  // Block time: 30 seconds = 2880 blocks/day
  // Block reward: ~29.4 FLUX (calculated from real network data)
  // Source: https://cloud.runonflux.com/dashboards/rewards
  private readonly BLOCK_TIME_SECONDS = 30;
  private readonly BLOCKS_PER_DAY = (24 * 60 * 60) / 30; // 2880 blocks/day
  private readonly PON_BLOCK_REWARD = 29.4; // ~29.4 FLUX per block (verified Nov 2025)

  // Tier reward percentages (of total block reward)
  // Source: https://cloud.runonflux.com/dashboards/rewards
  // STRATUS: 64.5%, NIMBUS: 25%, CUMULUS: 7%, Foundations: 3.5%
  private readonly TIER_REWARD_PERCENT = {
    CUMULUS: 0.07,    // 7% of block reward
    NIMBUS: 0.25,     // 25% of block reward
    STRATUS: 0.645    // 64.5% of block reward
  };

  // Collateral requirements
  private readonly TIER_COLLATERAL = {
    CUMULUS: 1000,
    NIMBUS: 12500,
    STRATUS: 40000
  };

  // Build API URL with proxy or direct
  private buildUrl(endpoint: string, params: Record<string, string> = {}): string {
    if (this.useProxy) {
      // Serverless function format: /api/flux?endpoint=daemon/xxx&param1=value1
      const queryParams = new URLSearchParams({ endpoint, ...params }).toString();
      return `${this.proxyUrl}?${queryParams}`;
    } else {
      // Direct API call (only in dev, will have CORS issues)
      const queryString = new URLSearchParams(params).toString();
      if (endpoint.startsWith('daemon/')) {
        return `https://api.runonflux.io/${endpoint}${queryString ? '?' + queryString : ''}`;
      } else if (endpoint.startsWith('apps/')) {
        return `https://api.runonflux.io/${endpoint}${queryString ? '?' + queryString : ''}`;
      } else if (endpoint.startsWith('explorer/')) {
        return `https://explorer.runonflux.io/api/${endpoint.replace('explorer/', '')}${queryString ? '?' + queryString : ''}`;
      } else if (endpoint.startsWith('stats/')) {
        return `https://stats.runonflux.io/${endpoint.replace('stats/', '')}${queryString ? '?' + queryString : ''}`;
      } else if (endpoint.startsWith('fusion/')) {
        return `https://fusion.runonflux.io/${endpoint.replace('fusion/', '')}${queryString ? '?' + queryString : ''}`;
      } else if (endpoint.startsWith('node/')) {
        // Direct node access (dev only, will likely fail due to CORS)
        const path = endpoint.replace('node/', '');
        const ip = params.ip || '';
        const port = params.port || '16127';
        return `http://${ip}:${port}/${path}`;
      }
      return '';
    }
  }

  // Standard fetch options based on 2ndtlmining/Fluxnode implementation
  private readonly fetchOptions = {
    credentials: 'omit' as RequestCredentials,
    headers: {
      'Accept': 'application/json, text/plain, */*',
      'Accept-Language': 'en-US,en;q=0.9'
    },
    method: 'GET'
  };

  /**
   * Fetch all nodes for a specific wallet address
   */
  async getNodesByWallet(walletAddress: string): Promise<FluxNode[]> {
    try {
      // Use daemon API with filter parameter (server-side filtering)
      // Based on 2ndtlmining/Fluxnode implementation
      const url = this.buildUrl('daemon/viewdeterministiczelnodelist', { filter: walletAddress });
      console.log(`Fetching nodes for wallet: ${walletAddress} from ${url}`);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const result: NodeListResponse = await response.json();

      // API returns {status: "success", data: [...]}
      if (result.status !== 'success' || !result.data) {
        throw new Error('Failed to fetch nodes from API');
      }

      const userNodes = Array.isArray(result.data) ? result.data : [];

      console.log(`Nodes found for wallet ${walletAddress}: ${userNodes.length}`);

      return userNodes;
    } catch (error) {
      console.error('Error fetching nodes for wallet:', walletAddress, error);
      throw error;
    }
  }

  /**
   * Fetch all nodes across multiple wallets
   */
  async getNodesByWallets(walletAddresses: string[]): Promise<FluxNode[]> {
    try {
      const nodesPromises = walletAddresses.map(address =>
        this.getNodesByWallet(address)
      );

      const nodesArrays = await Promise.all(nodesPromises);
      return nodesArrays.flat();
    } catch (error) {
      console.error('Error fetching nodes for multiple wallets:', error);
      throw error;
    }
  }

  /**
   * Fetch complete node list (for network statistics)
   */
  async getAllNodes(): Promise<FluxNode[]> {
    try {
      // Use daemon API without filter to get all nodes
      const url = this.buildUrl('daemon/viewdeterministiczelnodelist');
      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const result: NodeListResponse = await response.json();

      // API returns {status: "success", data: [...]}
      if (result.status !== 'success' || !result.data) {
        throw new Error('Failed to fetch all nodes');
      }

      return Array.isArray(result.data) ? result.data : [];
    } catch (error) {
      console.error('Error fetching all nodes:', error);
      throw error;
    }
  }

  /**
   * Get network statistics
   */
  async getNetworkStats(): Promise<NetworkStats> {
    try {
      const nodes = await this.getAllNodes();

      const cumulusCount = nodes.filter(n => n.tier === 'CUMULUS').length;
      const nimbusCount = nodes.filter(n => n.tier === 'NIMBUS').length;
      const stratusCount = nodes.filter(n => n.tier === 'STRATUS').length;

      const totalCollateral =
        cumulusCount * 1000 +
        nimbusCount * 12500 +
        stratusCount * 40000;

      // Get current block height from first node
      const blockHeight = nodes[0]?.last_confirmed_height || 0;

      // Fetch FLUX price from CoinGecko
      const fluxPrice = await this.getFluxPrice();

      return {
        totalNodes: nodes.length,
        cumulusCount,
        nimbusCount,
        stratusCount,
        totalCollateral,
        blockHeight,
        fluxPrice
      };
    } catch (error) {
      console.error('Error fetching network stats:', error);
      throw error;
    }
  }

  /**
   * Get current FLUX price from CoinGecko
   */
  async getFluxPrice(): Promise<number> {
    try {
      const data = await callCoinGeckoAPI('/simple/price', {
        ids: 'zelcash',
        vs_currencies: 'usd'
      });
      return data.zelcash?.usd || 0;
    } catch (error) {
      console.error('Error fetching FLUX price:', error);
      return 0;
    }
  }

  /**
   * Get benchmark data for a specific node
   */
  async getNodeBenchmark(ip: string): Promise<BenchmarkData | null> {
    try {
      const response = await fetch(`http://${ip}:16127/flux/benchmark`);

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      return result.data || null;
    } catch (error) {
      console.error(`Error fetching benchmark for ${ip}:`, error);
      return null;
    }
  }

  /**
   * Get latest benchmarks from /benchmark/getbenchmarks endpoint
   * This returns the most recent benchmark results for the node
   * Only fetched when expanded details are shown (lazy loading)
   */
  async getLatestBenchmarks(ip: string): Promise<{
    status: string;
    benchmarkTier: string;
    eps: number;
    ping: number;
    download: number;
    upload: number;
    cores: number;
    ram: number;
    ssd: number;
    hdd: number;
    ddWrite: number;
    totalStorage: number;
    disks: string;
    time: number;
  } | null> {
    try {
      const nodeIp = ip.split(':')[0];
      const port = ip.split(':')[1] || '16127';

      const url = this.buildUrl('node/benchmark/getbenchmarks', { ip: nodeIp, port });

      const response = await fetch(url, {
        ...this.fetchOptions,
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      const data = result.data || result;

      if (!data) {
        return null;
      }

      console.log('[FluxNode] Latest benchmarks raw:', JSON.stringify(data).substring(0, 500));

      return {
        status: data.status || '',
        benchmarkTier: data.benchmarkTier || data.tier || '',
        eps: data.eps || 0,
        ping: data.ping || 0,
        download: data.download || data.downloadspeed || 0,
        upload: data.upload || data.uploadspeed || 0,
        cores: data.cores || data.realcores || 0,
        ram: data.ram || 0,
        ssd: data.ssd || 0,
        hdd: data.hdd || 0,
        ddWrite: data.ddwrite || data.ddWrite || 0,
        totalStorage: data.totalstorage || data.totalStorage || 0,
        disks: data.disks || '',
        time: data.time || 0
      };
    } catch (error) {
      console.log(`[FluxNode] Could not fetch latest benchmarks for ${ip}:`, error);
      return null;
    }
  }

  /**
   * Extended node info from /flux/info endpoint
   */
  async getNodeInfo(ip: string): Promise<{
    fluxVersion: string;
    nodeVersion: string;
    // Hardware specs
    cores: number;
    ram: number; // GB
    ssd: number; // GB
    // Benchmark
    cpuBench: number;
    ramBench: number;
    ssdWrite: number;
    downloadSpeed: number;
    uploadSpeed: number;
    // Geolocation
    country: string;
    countryCode: string;
    continent: string;
    city: string;
    org: string;
    // Uptime
    uptimeSeconds: number;
  } | null> {
    try {
      const nodeIp = ip.split(':')[0];
      const port = ip.split(':')[1] || '16127';

      const url = this.buildUrl('node/flux/info', { ip: nodeIp, port });

      const response = await fetch(url, {
        ...this.fetchOptions,
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      const data = result.data || result;
      const fluxData = data?.flux;
      // Benchmark data can be in benchmark.bench or directly in benchmark
      const benchmarkData = data?.benchmark?.bench || data?.benchmark;
      const geolocation = data?.geolocation;
      const node = data?.node;
      const daemon = data?.daemon;

      // Debug log to see actual structure and values
      console.log('[FluxNode] Node info response keys:', data ? Object.keys(data) : []);
      console.log('[FluxNode] Benchmark keys:', benchmarkData ? Object.keys(benchmarkData) : []);
      console.log('[FluxNode] Benchmark.bench exists:', !!data?.benchmark?.bench);
      console.log('[FluxNode] Raw benchmark data sample:', JSON.stringify(benchmarkData || {}).substring(0, 500));

      if (fluxData?.version) {
        // RAM: check if value is already in GB (< 1000) or MB (needs conversion)
        let ramGB = 0;
        if (benchmarkData?.ram) {
          ramGB = benchmarkData.ram > 1000 ? Math.round(benchmarkData.ram / 1024) : benchmarkData.ram;
        }

        // Network speeds - check multiple locations and field names
        // Flux API stores speeds in bytes/sec typically
        let downloadSpeed = 0;
        let uploadSpeed = 0;

        // Check benchmark data for various field names
        if (benchmarkData) {
          downloadSpeed = benchmarkData.downloadspeed || benchmarkData.download_speed ||
                         benchmarkData.down_speed || benchmarkData.downloadSpeed ||
                         benchmarkData.dws || benchmarkData.down || 0;
          uploadSpeed = benchmarkData.uploadspeed || benchmarkData.upload_speed ||
                       benchmarkData.up_speed || benchmarkData.uploadSpeed ||
                       benchmarkData.uws || benchmarkData.up || 0;
        }

        // Log what we found for debugging
        console.log('[FluxNode] Speed fields found:', { downloadSpeed, uploadSpeed });

        // Uptime - check multiple locations
        // Can be in flux.uptime, node.uptime, daemon.uptime, or benchmark.time
        let uptimeSeconds = 0;
        if (fluxData?.uptime && fluxData.uptime > 0) {
          uptimeSeconds = fluxData.uptime;
        } else if (node?.uptime && node.uptime > 0) {
          uptimeSeconds = node.uptime;
        } else if (daemon?.uptime && daemon.uptime > 0) {
          uptimeSeconds = daemon.uptime;
        } else if (benchmarkData?.time && benchmarkData.time > 0) {
          // benchmark.time is timestamp, convert to uptime
          uptimeSeconds = Math.floor(Date.now() / 1000) - benchmarkData.time;
        }

        console.log('[FluxNode] Uptime found:', uptimeSeconds, 'from flux:', fluxData?.uptime, 'node:', node?.uptime);

        return {
          fluxVersion: fluxData.version || '',
          nodeVersion: node?.version || '',
          // Hardware specs from benchmark.bench
          cores: benchmarkData?.cores || benchmarkData?.realcores || 0,
          ram: ramGB,
          ssd: benchmarkData?.totalstorage || benchmarkData?.ssd || benchmarkData?.hdd || 0,
          // Benchmark scores
          cpuBench: benchmarkData?.eps || 0,
          ramBench: benchmarkData?.rambench || 0,
          ssdWrite: benchmarkData?.ddwrite || benchmarkData?.ddrwrite || 0,
          // Network speeds in bytes/sec
          downloadSpeed,
          uploadSpeed,
          // Geolocation
          country: geolocation?.country || '',
          countryCode: geolocation?.countryCode || geolocation?.country_code || geolocation?.cc || '',
          continent: geolocation?.continent || '',
          city: geolocation?.city || geolocation?.region || '',
          org: geolocation?.org || geolocation?.isp || '',
          // Uptime in seconds
          uptimeSeconds
        };
      }
      return null;
    } catch (error) {
      console.log(`[FluxNode] Could not fetch node info for ${ip}:`, error);
      return null;
    }
  }

  /**
   * Get system uptime for PNR (Progressive Node Rewards) eligibility
   * Fetches from /flux/systemuptime endpoint on the node via proxy
   *
   * PNR Requirements:
   * - System uptime must be > 1 month (2,592,000 seconds)
   * - Node must be running ArcaneOS
   * - More than 21,600 blocks confirmed
   *
   * Returns:
   * - systemUptime: OS uptime in seconds
   * - pnrEligible: whether the node meets the 1-month uptime requirement
   * - uptimeFormatted: human-readable uptime string
   * - daysUntilEligible: days remaining if not yet eligible
   */
  async getNodeSystemUptime(ip: string): Promise<{
    systemUptime: number;
    pnrEligible: boolean;
    uptimeFormatted: string;
    daysUntilEligible: number;
    uptimePercent: number; // Percentage towards 30-day requirement
  } | null> {
    try {
      const nodeIp = ip.split(':')[0];
      const port = ip.split(':')[1] || '16127';

      const url = this.buildUrl('node/flux/systemuptime', { ip: nodeIp, port });

      const response = await fetch(url, {
        ...this.fetchOptions,
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      // The API returns { status: 'success', data: <uptime_in_seconds> }
      const systemUptime = result.data || result.uptime || result || 0;

      // PNR requires 1 month (30 days) of uptime = 2,592,000 seconds
      const PNR_UPTIME_REQUIREMENT = 30 * 24 * 60 * 60; // 2,592,000 seconds

      const pnrEligible = systemUptime >= PNR_UPTIME_REQUIREMENT;
      const remainingSeconds = Math.max(0, PNR_UPTIME_REQUIREMENT - systemUptime);
      const daysUntilEligible = Math.ceil(remainingSeconds / (24 * 60 * 60));
      const uptimePercent = Math.min(100, Math.round((systemUptime / PNR_UPTIME_REQUIREMENT) * 100));

      // Format uptime in human readable format
      const days = Math.floor(systemUptime / 86400);
      const hours = Math.floor((systemUptime % 86400) / 3600);
      const minutes = Math.floor((systemUptime % 3600) / 60);

      let uptimeFormatted = '';
      if (days > 0) {
        uptimeFormatted = `${days}j ${hours}h`;
      } else if (hours > 0) {
        uptimeFormatted = `${hours}h ${minutes}m`;
      } else {
        uptimeFormatted = `${minutes}m`;
      }

      return {
        systemUptime,
        pnrEligible,
        uptimeFormatted,
        daysUntilEligible,
        uptimePercent
      };
    } catch (error) {
      console.log(`[FluxNode] Could not fetch system uptime for ${ip}:`, error);
      return null;
    }
  }

  /**
   * Get installed apps on a specific node
   * Fetches from /apps/installedapps endpoint on the node via proxy
   */
  async getNodeApps(ip: string): Promise<{
    count: number;
    apps: Array<{
      name: string;
      description?: string;
      ports?: number[];
      domains?: string[];
    }>;
  } | null> {
    try {
      const nodeIp = ip.split(':')[0];
      const port = ip.split(':')[1] || '16127';

      const url = this.buildUrl('node/apps/installedapps', { ip: nodeIp, port });

      const response = await fetch(url, {
        ...this.fetchOptions,
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      const appsData = result.data || result || [];

      if (Array.isArray(appsData)) {
        return {
          count: appsData.length,
          apps: appsData.map((app: any) => ({
            name: app.name || app.Names?.[0]?.replace('/', '') || 'Unknown',
            description: app.description || '',
            ports: app.Ports?.map((p: any) => p.PublicPort).filter(Boolean) || [],
            domains: app.domains || []
          }))
        };
      }
      return { count: 0, apps: [] };
    } catch (error) {
      console.log(`[FluxNode] Could not fetch apps for ${ip}:`, error);
      return null;
    }
  }

  /**
   * Build app URL for a Flux app
   * Apps are accessible via: https://[appname].app.runonflux.io or http://[nodeip]:[port]
   */
  buildAppUrl(appName: string, nodeIp: string, port?: number): string {
    // Primary URL via Flux network
    const cleanName = appName.toLowerCase().replace(/[^a-z0-9-]/g, '');
    return `https://${cleanName}.app.runonflux.io`;
  }

  /**
   * Calculate node uptime percentage
   */
  calculateUptime(node: FluxNode): number {
    const confirmedHeight = node.confirmed_height || 0;
    const lastConfirmedHeight = node.last_confirmed_height || 0;

    if (confirmedHeight === 0) return 0;

    const totalBlocks = lastConfirmedHeight - confirmedHeight;
    if (totalBlocks === 0) return 100;

    // Estimate missed blocks (simplified calculation)
    const missedBlocks = Math.max(0, totalBlocks - (node.last_paid_height - confirmedHeight));
    const uptime = ((totalBlocks - missedBlocks) / totalBlocks) * 100;

    return Math.min(100, Math.max(0, uptime));
  }

  /**
   * Determine node status
   *
   * IMPORTANT: If a node is returned by the Flux API (viewdeterministiczelnodelist),
   * it means the node IS active on the network. The payment timing has nothing to do
   * with online/offline status - it depends on the node's rank in the payment queue.
   *
   * Status logic:
   * - CONFIRMED: Node is confirmed and active (confirmed_height > 0)
   * - STARTED: Node just started, not yet confirmed (confirmed_height === 0)
   */
  getNodeStatus(node: FluxNode): 'CONFIRMED' | 'STARTED' | 'OFFLINE' | 'EXPIRED' {
    const confirmedHeight = node.confirmed_height || 0;

    // If never confirmed, node just started
    if (confirmedHeight === 0) {
      return 'STARTED';
    }

    // If node is in the API response and has been confirmed, it's active
    // The API only returns active nodes from the deterministic list
    return 'CONFIRMED';
  }

  /**
   * Calculate estimated rewards using real network data
   * This now uses the correct tier percentages and requires node counts
   */
  calculateRewards(
    node: FluxNode,
    fluxPrice: number,
    tierNodeCounts?: { CUMULUS: number; NIMBUS: number; STRATUS: number }
  ): {
    daily: number;
    weekly: number;
    monthly: number;
    yearly: number;
    roi: number;
  } {
    const tier = node.tier as 'CUMULUS' | 'NIMBUS' | 'STRATUS';
    const collateral = this.TIER_COLLATERAL[tier] || 0;

    // If we have real tier node counts, use accurate calculation
    if (tierNodeCounts && tierNodeCounts[tier] > 0) {
      const nodeCount = tierNodeCounts[tier];

      // Total daily FLUX from all blocks
      const totalDailyRewards = this.BLOCKS_PER_DAY * this.PON_BLOCK_REWARD;

      // Reward percentage for this tier
      const tierPercent = this.TIER_REWARD_PERCENT[tier];

      // Daily reward per node of this tier
      const daily = (totalDailyRewards * tierPercent) / nodeCount;
      const weekly = daily * 7;
      const monthly = daily * 30;
      const yearly = daily * 365;

      // Calculate ROI
      const roi = collateral > 0 ? (yearly / collateral) * 100 : 0;

      return { daily, weekly, monthly, yearly, roi };
    }

    // Fallback: Use estimated node counts if not provided
    // Based on current network: ~12000 CUMULUS, ~1800 NIMBUS, ~1550 STRATUS
    const estimatedNodeCounts = {
      CUMULUS: 12000,
      NIMBUS: 1800,
      STRATUS: 1550
    };

    const nodeCount = estimatedNodeCounts[tier] || 1;

    // Total daily FLUX from all blocks
    const totalDailyRewards = this.BLOCKS_PER_DAY * this.PON_BLOCK_REWARD;

    // Reward percentage for this tier
    const tierPercent = this.TIER_REWARD_PERCENT[tier];

    // Daily reward per node of this tier
    const daily = (totalDailyRewards * tierPercent) / nodeCount;
    const weekly = daily * 7;
    const monthly = daily * 30;
    const yearly = daily * 365;

    // Calculate ROI
    const roi = collateral > 0 ? (yearly / collateral) * 100 : 0;

    return { daily, weekly, monthly, yearly, roi };
  }

  /**
   * Get wallet balance
   */
  async getWalletBalance(address: string): Promise<number> {
    try {
      const url = this.buildUrl('explorer/addr/' + address + '/balance');
      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        throw new Error('Failed to fetch wallet balance');
      }

      const balance = await response.json();
      return balance / 100000000; // Satoshis to FLUX
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      return 0;
    }
  }

  /**
   * Get node count by tier (for APY calculation)
   * Uses daemon/getzelnodecount like 2ndtlmining/Fluxnode does
   * API returns: { status: "success", data: { "cumulus-enabled": N, "nimbus-enabled": N, "stratus-enabled": N, total: N } }
   */
  async getNodeCount(): Promise<NodeCountResponse['data'] | null> {
    try {
      const url = this.buildUrl('daemon/getzelnodecount');
      console.log('[FluxNode] Fetching node counts from:', url);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        throw new Error(`Failed to fetch node count: ${response.status}`);
      }

      const result = await response.json();
      console.log('[FluxNode] Full API response:', JSON.stringify(result));

      // API returns: { status: "success", data: { "cumulus-enabled": N, ... } }
      if (result.status !== 'success' || !result.data) {
        console.error('[FluxNode] Invalid response format:', result);
        return null;
      }

      const data = result.data;

      // Log all available keys to debug
      console.log('[FluxNode] Response data keys:', Object.keys(data));

      // Try multiple key variations (API might use different formats)
      const getCumulusCount = () => {
        return data['cumulus-enabled'] || data['cumulus'] || data['CUMULUS'] ||
               data['cumulusEnabled'] || data['cumulus_enabled'] || 0;
      };
      const getNimbusCount = () => {
        return data['nimbus-enabled'] || data['nimbus'] || data['NIMBUS'] ||
               data['nimbusEnabled'] || data['nimbus_enabled'] || 0;
      };
      const getStratusCount = () => {
        return data['stratus-enabled'] || data['stratus'] || data['STRATUS'] ||
               data['stratusEnabled'] || data['stratus_enabled'] || 0;
      };

      let cumulus = Number(getCumulusCount()) || 0;
      let nimbus = Number(getNimbusCount()) || 0;
      let stratus = Number(getStratusCount()) || 0;
      const total = Number(data.total) || 0;

      console.log('[FluxNode] Extracted counts:', { cumulus, nimbus, stratus, total });

      // Validation: STRATUS should be 300-600, NIMBUS 1000-2000, CUMULUS 10000-20000
      // If values seem wrong, try to use reasonable estimates based on network stats
      if (stratus > 1000 || stratus === 0) {
        console.warn('[FluxNode] STRATUS count seems wrong:', stratus);
        // Try fetching from alternative source or use estimates
        // Based on fluxnode.app and other trackers, typical counts are:
        // This is a fallback - real API should provide correct values
        if (total > 10000 && cumulus === 0 && nimbus === 0 && stratus === 0) {
          // API might return only total, estimate tier distribution
          // Typical distribution: ~85% CUMULUS, ~10% NIMBUS, ~3% STRATUS
          cumulus = Math.round(total * 0.85);
          nimbus = Math.round(total * 0.10);
          stratus = Math.round(total * 0.03);
          console.log('[FluxNode] Using estimated tier distribution:', { cumulus, nimbus, stratus });
        }
      }

      const nodeCounts = {
        'cumulus-enabled': cumulus,
        'nimbus-enabled': nimbus,
        'stratus-enabled': stratus,
        total: total || (cumulus + nimbus + stratus)
      };

      console.log('[FluxNode] Final node counts:', nodeCounts);

      return nodeCounts;
    } catch (error) {
      console.error('[FluxNode] Error fetching node count:', error);
      return null;
    }
  }

  /**
   * Get current block height from daemon/getinfo
   * Used for maintenance window calculation
   */
  async getCurrentBlockHeight(): Promise<number> {
    try {
      const url = this.buildUrl('daemon/getinfo');
      console.log('[FluxNode] Fetching current block height from:', url);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        throw new Error(`Failed to fetch block height: ${response.status}`);
      }

      const result = await response.json();

      // API returns: { status: "success", data: { blocks: N, ... } }
      if (result.status !== 'success' || !result.data) {
        console.error('[FluxNode] Invalid getinfo response:', result);
        return 0;
      }

      const blockHeight = result.data.blocks || result.data.height || 0;
      console.log('[FluxNode] Current block height:', blockHeight);

      return blockHeight;
    } catch (error) {
      console.error('[FluxNode] Error fetching block height:', error);
      return 0;
    }
  }

  /**
   * Calculate maintenance window for a node
   * Based on 2ndtlmining/Fluxnode implementation
   *
   * 480 blocks at 30 seconds per block = 240 minutes (4 hours) maintenance window
   * Returns formatted time remaining or 'Closed' if window is closed
   */
  calculateMaintenanceWindow(lastConfirmedHeight: number, currentBlockHeight: number): {
    status: 'open' | 'closed';
    formatted: string;
    minutesRemaining: number;
  } {
    const BLOCK_RATE = 480; // 480 blocks = 240 minutes maintenance window

    if (!currentBlockHeight || !lastConfirmedHeight) {
      return { status: 'open', formatted: '-', minutesRemaining: 0 };
    }

    const blocksSinceConfirmed = currentBlockHeight - lastConfirmedHeight;
    const blocksRemaining = BLOCK_RATE - blocksSinceConfirmed;

    if (blocksRemaining <= 0) {
      return { status: 'closed', formatted: 'Closed', minutesRemaining: 0 };
    }

    // Convert blocks to minutes (1 block = 30 seconds = 0.5 minutes)
    const minutesRemaining = blocksRemaining / 2;

    return {
      status: 'open',
      formatted: this.formatMaintenanceTime(minutesRemaining),
      minutesRemaining
    };
  }

  /**
   * Format maintenance time into human-readable format
   * Based on 2ndtlmining/Fluxnode format_minutes function
   */
  private formatMaintenanceTime(minutes: number): string {
    if (minutes < 1) {
      return `${Math.round(minutes * 60)} secs`;
    }

    if (minutes < 60) {
      return `${Math.round(minutes)} mins`;
    }

    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);

    if (mins === 0) {
      return `${hours} Hrs`;
    }

    return `${hours} Hrs`;
  }

  /**
   * Calculate real APY for each tier based on network stats
   * Formula: (Daily Reward per Node * 365 / Collateral) * 100
   *
   * Flux reward distribution:
   * - CUMULUS: 7.5% of block reward
   * - NIMBUS: 12.5% of block reward
   * - STRATUS: 30% of block reward
   * - Miners: 50% of block reward
   */
  calculateTierAPY(tier: 'CUMULUS' | 'NIMBUS' | 'STRATUS', nodeCount: number): number {
    if (nodeCount === 0) return 0;

    // Total daily FLUX from blocks
    const totalDailyRewards = this.BLOCKS_PER_DAY * this.PON_BLOCK_REWARD;

    // Reward percentage for this tier (already represents % of total block)
    const tierPercent = this.TIER_REWARD_PERCENT[tier];

    // Daily reward per node of this tier
    const dailyRewardPerNode = (totalDailyRewards * tierPercent) / nodeCount;

    // Yearly reward
    const yearlyReward = dailyRewardPerNode * 365;

    // APY = (Yearly Reward / Collateral) * 100
    const collateral = this.TIER_COLLATERAL[tier];
    const apy = (yearlyReward / collateral) * 100;

    return apy;
  }

  /**
   * Get extended network stats including real APY and current block height
   */
  async getExtendedNetworkStats(): Promise<ExtendedNetworkStats> {
    try {
      // Fetch node counts, flux price, and current block height in parallel
      const [nodeCount, fluxPrice, blockHeight] = await Promise.all([
        this.getNodeCount(),
        this.getFluxPrice(),
        this.getCurrentBlockHeight()
      ]);

      if (!nodeCount) {
        throw new Error('Failed to get node count');
      }

      const cumulusCount = nodeCount['cumulus-enabled'] || 0;
      const nimbusCount = nodeCount['nimbus-enabled'] || 0;
      const stratusCount = nodeCount['stratus-enabled'] || 0;

      // Calculate APY for each tier
      const cumulusAPY = this.calculateTierAPY('CUMULUS', cumulusCount);
      const nimbusAPY = this.calculateTierAPY('NIMBUS', nimbusCount);
      const stratusAPY = this.calculateTierAPY('STRATUS', stratusCount);

      const totalCollateral =
        cumulusCount * this.TIER_COLLATERAL.CUMULUS +
        nimbusCount * this.TIER_COLLATERAL.NIMBUS +
        stratusCount * this.TIER_COLLATERAL.STRATUS;

      return {
        totalNodes: nodeCount.total || cumulusCount + nimbusCount + stratusCount,
        cumulusCount,
        nimbusCount,
        stratusCount,
        totalCollateral,
        blockHeight, // Current block height from daemon/getinfo
        fluxPrice,
        cumulusAPY,
        nimbusAPY,
        stratusAPY,
        blocksPerDay: this.BLOCKS_PER_DAY,
        rewardPerBlock: this.PON_BLOCK_REWARD
      };
    } catch (error) {
      console.error('Error fetching extended network stats:', error);
      throw error;
    }
  }

  /**
   * Calculate real rewards for a node based on network stats
   *
   * Flux reward distribution per block:
   * - CUMULUS: 7.5% of 14 FLUX = 1.05 FLUX (shared among all CUMULUS nodes)
   * - NIMBUS: 12.5% of 14 FLUX = 1.75 FLUX (shared among all NIMBUS nodes)
   * - STRATUS: 30% of 14 FLUX = 4.2 FLUX (shared among all STRATUS nodes)
   * - Miners: 50% = 7 FLUX
   */
  calculateRealRewards(
    tier: 'CUMULUS' | 'NIMBUS' | 'STRATUS',
    tierNodeCount: number,
    fluxPrice: number
  ): {
    daily: number;
    weekly: number;
    monthly: number;
    yearly: number;
    roi: number;
    dailyUSD: number;
    monthlyUSD: number;
    yearlyUSD: number;
  } {
    if (tierNodeCount === 0) {
      return { daily: 0, weekly: 0, monthly: 0, yearly: 0, roi: 0, dailyUSD: 0, monthlyUSD: 0, yearlyUSD: 0 };
    }

    // Total daily FLUX from all blocks
    const totalDailyRewards = this.BLOCKS_PER_DAY * this.PON_BLOCK_REWARD;

    // Reward percentage for this tier (already represents % of total block)
    const tierPercent = this.TIER_REWARD_PERCENT[tier];

    // Daily reward per node of this tier
    const daily = (totalDailyRewards * tierPercent) / tierNodeCount;
    const weekly = daily * 7;
    const monthly = daily * 30;
    const yearly = daily * 365;

    // Calculate ROI
    const collateral = this.TIER_COLLATERAL[tier];
    const roi = (yearly / collateral) * 100;

    return {
      daily,
      weekly,
      monthly,
      yearly,
      roi,
      dailyUSD: daily * fluxPrice,
      monthlyUSD: monthly * fluxPrice,
      yearlyUSD: yearly * fluxPrice
    };
  }

  /**
   * Get Parallel Assets summary for a wallet address
   * Uses Fusion API: https://fusion.runonflux.io/coinbase/summary?address=xxx
   * Also tries alternate endpoints if main one fails
   */
  async getParallelAssets(walletAddress: string): Promise<ParallelAssetsSummary | null> {
    try {
      // Try the coinbase/summary endpoint first
      const url = this.buildUrl('fusion/coinbase/summary', { address: walletAddress });
      console.log(`[FluxNode] Fetching parallel assets for wallet: ${walletAddress}`);
      console.log(`[FluxNode] PA URL: ${url}`);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        console.error('[FluxNode] Failed to fetch parallel assets:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('[FluxNode] PA raw response:', JSON.stringify(data).substring(0, 500));

      // Parse the Fusion API response
      // Format can be: { status: 'success', data: { ... } } or direct object
      if (!data) {
        console.log('[FluxNode] No parallel assets data available');
        return null;
      }

      // Map the response to our interface
      const assets: ParallelAssetDetail[] = [];
      const chainMapping: Record<string, ParallelChain> = {
        'kadena': 'KDA',
        'kda': 'KDA',
        'ethereum': 'ETH',
        'eth': 'ETH',
        'bsc': 'BSC',
        'binance': 'BSC',
        'tron': 'TRX',
        'trx': 'TRX',
        'solana': 'SOL',
        'sol': 'SOL',
        'avalanche': 'AVAX',
        'avax': 'AVAX',
        'polygon': 'MATIC',
        'matic': 'MATIC',
        'base': 'ETH',
        'ergo': 'ETH',
        'algorand': 'SOL'
      };

      let totalClaimable = 0;
      let totalClaimed = 0;
      let totalReceived = 0;

      // Handle different response structures
      const responseData = data.data || data;

      // Check if it's an array of assets or object with chain keys
      if (Array.isArray(responseData)) {
        // Array format: [{ chain: 'KDA', claimable: 0.1, ... }, ...]
        for (const item of responseData) {
          const chainKey = (item.chain || item.name || '').toLowerCase();
          const chain = chainMapping[chainKey];
          if (chain) {
            const claimable = parseFloat(item.claimable || item.pending || item.available || '0');
            const claimed = parseFloat(item.claimed || item.sent || item.withdrawn || '0');
            const received = parseFloat(item.received || item.mined || item.total || '0');

            totalClaimable += claimable;
            totalClaimed += claimed;
            totalReceived += received;

            if (claimable > 0 || claimed > 0 || received > 0) {
              assets.push({
                chain,
                symbol: item.symbol || chain,
                claimable,
                claimed,
                received,
                pending: claimable
              });
            }
          }
        }
      } else if (typeof responseData === 'object') {
        // Object format: { kadena: { claimable: 0.1, ... }, ethereum: { ... } }
        for (const [chainName, chainData] of Object.entries(responseData)) {
          const chain = chainMapping[chainName.toLowerCase()];
          if (chain && chainData && typeof chainData === 'object') {
            const cd = chainData as any;
            const claimable = parseFloat(cd.claimable || cd.pending || cd.available || '0');
            const claimed = parseFloat(cd.claimed || cd.sent || cd.withdrawn || '0');
            const received = parseFloat(cd.received || cd.mined || cd.total || '0');

            totalClaimable += claimable;
            totalClaimed += claimed;
            totalReceived += received;

            if (claimable > 0 || claimed > 0 || received > 0) {
              assets.push({
                chain,
                symbol: cd.symbol || chainName.toUpperCase(),
                claimable,
                claimed,
                received,
                pending: claimable
              });
            }
          }
        }
      }

      console.log(`[FluxNode] PA parsed: ${assets.length} assets, totalClaimable=${totalClaimable}`);

      return {
        address: walletAddress,
        totalClaimable,
        totalClaimed,
        totalReceived,
        fusionFees: 0,
        assets
      };
    } catch (error) {
      console.error('Error fetching parallel assets:', error);
      return null;
    }
  }

  /**
   * Get Fusion mining fees
   */
  async getFusionFees(): Promise<Record<string, number>> {
    try {
      const url = this.buildUrl('fusion/fees');
      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        return {};
      }

      const data = await response.json();
      return data.data || data || {};
    } catch (error) {
      console.error('Error fetching fusion fees:', error);
      return {};
    }
  }

  /**
   * Get deployed applications by owner wallet address
   * Uses Flux Apps API: /apps/globalappsspecifications (fetches all, then filters by owner)
   * Returns all apps deployed by a specific wallet with full specifications
   */
  async getDeployedAppsByOwner(walletAddress: string): Promise<FluxDeployedAppsSummary | null> {
    try {
      // Fetch all apps and filter by owner client-side
      // The path parameter filtering (/apps/globalappsspecifications/:hash/:owner) doesn't work well with proxies
      const url = this.buildUrl('apps/globalappsspecifications');
      console.log(`[FluxNode] Fetching all apps to filter by wallet: ${walletAddress}`);
      console.log(`[FluxNode] URL: ${url}`);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        console.error('[FluxNode] Failed to fetch apps:', response.status, response.statusText);
        return null;
      }

      const result = await response.json();
      console.log('[FluxNode] Global apps response status:', result.status);
      console.log('[FluxNode] Global apps count:', Array.isArray(result.data) ? result.data.length : 'N/A');

      // API returns { status: "success", data: [...] }
      if (result.status !== 'success' || !result.data) {
        console.log('[FluxNode] No apps found or invalid response');
        return {
          owner: walletAddress,
          totalApps: 0,
          totalInstances: 0,
          apps: [],
          totalCpu: 0,
          totalRam: 0,
          totalHdd: 0
        };
      }

      // Filter apps by owner
      const ownerApps = result.data.filter((app: any) => app.owner === walletAddress);
      console.log(`[FluxNode] Found ${ownerApps.length} apps for owner ${walletAddress}`);

      if (ownerApps.length === 0) {
        return {
          owner: walletAddress,
          totalApps: 0,
          totalInstances: 0,
          apps: [],
          totalCpu: 0,
          totalRam: 0,
          totalHdd: 0
        };
      }

      // Use the filtered apps for processing
      const appsToProcess = ownerApps;

      const apps: FluxDeployedApp[] = [];
      let totalCpu = 0;
      let totalRam = 0;
      let totalHdd = 0;
      let totalInstances = 0;

      // Parse each app specification
      for (const app of appsToProcess) {
        // Parse compose array (app components)
        const compose: FluxAppComponent[] = [];
        if (app.compose && Array.isArray(app.compose)) {
          for (const comp of app.compose) {
            const component: FluxAppComponent = {
              name: comp.name || '',
              description: comp.description || '',
              repotag: comp.repotag || '',
              owner: comp.owner || walletAddress,
              ports: comp.ports || [],
              domains: comp.domains || [],
              environmentParameters: comp.environmentParameters || comp.enviromentParameters || [],
              commands: comp.commands || [],
              containerPorts: comp.containerPorts || [],
              containerData: comp.containerData || '',
              cpu: comp.cpu || 0,
              ram: comp.ram || 0,
              hdd: comp.hdd || 0,
              tiered: comp.tiered || false,
              secrets: comp.secrets,
              repoauth: comp.repoauth
            };
            compose.push(component);
            totalCpu += component.cpu;
            totalRam += component.ram;
            totalHdd += component.hdd;
          }
        }

        const instances = app.instances || app.nodes?.length || 1;
        totalInstances += instances;

        const deployedApp: FluxDeployedApp = {
          name: app.name || '',
          description: app.description || '',
          owner: app.owner || walletAddress,
          hash: app.hash || '',
          height: app.height || 0,
          version: app.version || 1,
          instances,
          compose,
          contacts: app.contacts || [],
          geolocation: app.geolocation || [],
          expire: app.expire || 0,
          nodes: app.nodes || [],
          staticip: app.staticip
        };

        apps.push(deployedApp);
      }

      console.log(`[FluxNode] Found ${apps.length} deployed apps for wallet ${walletAddress}`);

      return {
        owner: walletAddress,
        totalApps: apps.length,
        totalInstances,
        apps,
        totalCpu,
        totalRam,
        totalHdd
      };
    } catch (error) {
      console.error('[FluxNode] Error fetching deployed apps:', error);
      return null;
    }
  }

  /**
   * Get app locations/instances - where an app is running
   * Uses: /apps/applocations/:appname
   */
  async getAppLocations(appName: string): Promise<FluxAppInstance[]> {
    try {
      const url = this.buildUrl(`apps/applocations/${appName}`);
      console.log(`[FluxNode] Fetching app locations for: ${appName}`);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        console.error('[FluxNode] Failed to fetch app locations:', response.status);
        return [];
      }

      const result = await response.json();

      if (result.status !== 'success' || !result.data) {
        return [];
      }

      const instances: FluxAppInstance[] = [];
      const locations = Array.isArray(result.data) ? result.data : [];

      for (const loc of locations) {
        instances.push({
          name: appName,
          ip: loc.ip || loc.nodeip || '',
          broadcastedAt: loc.broadcastedAt || '',
          expireAt: loc.expireAt || '',
          hash: loc.hash || ''
        });
      }

      return instances;
    } catch (error) {
      console.error('[FluxNode] Error fetching app locations:', error);
      return [];
    }
  }

  /**
   * Get all registered apps on the network with their owners
   * Uses: /apps/globalappsspecifications (returns all apps)
   */
  async getRunningApps(): Promise<{ name: string; hash: string; instances: number; owner: string; description: string }[]> {
    try {
      const url = this.buildUrl('apps/globalappsspecifications');
      console.log('[FluxNode] Fetching global app specifications from:', url);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        console.error('[FluxNode] Failed to fetch global apps:', response.status, response.statusText);
        return [];
      }

      const result = await response.json();
      console.log('[FluxNode] Global apps response status:', result.status);
      console.log('[FluxNode] Global apps response keys:', Object.keys(result));

      // Handle different response formats
      let appsData: any[] = [];

      // Standard format: { status: "success", data: [...] }
      if (result.status === 'success' && result.data) {
        appsData = Array.isArray(result.data) ? result.data : [];
      }
      // Direct array response
      else if (Array.isArray(result)) {
        appsData = result;
      }
      // Try to find any array in the response
      else {
        for (const key of Object.keys(result)) {
          if (Array.isArray(result[key]) && result[key].length > 0) {
            appsData = result[key];
            console.log(`[FluxNode] Found apps array under key: ${key}`);
            break;
          }
        }
      }

      console.log(`[FluxNode] Found ${appsData.length} global apps`);

      if (appsData.length > 0) {
        // Debug: log first app structure
        console.log('[FluxNode] First global app structure:', JSON.stringify(appsData[0]).slice(0, 400));
      }

      // Map apps - globalappsspecifications returns full app specs
      return appsData.map((app: any) => ({
        name: app.name || '',
        hash: app.hash || '',
        instances: app.instances || 0,
        owner: app.owner || '',
        description: app.description || ''
      })).filter(app => app.name && app.owner); // Filter out apps without names or owners
    } catch (error) {
      console.error('[FluxNode] Error fetching global apps:', error);
      return [];
    }
  }

  /**
   * Get full app specification by name
   * Uses: /apps/appspecifications/:appname
   */
  async getAppSpecification(appName: string): Promise<FluxDeployedApp | null> {
    try {
      const url = this.buildUrl(`apps/appspecifications/${appName}`);
      console.log(`[FluxNode] Fetching app spec for: ${appName}`);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        console.log(`[FluxNode] App spec failed for ${appName}: ${response.status}`);
        return null;
      }

      const result = await response.json();
      console.log(`[FluxNode] App spec response for ${appName}:`, JSON.stringify(result).slice(0, 200));

      // Handle different response formats
      let app: any = null;

      if (result.status === 'success' && result.data) {
        app = result.data;
      } else if (result.name || result.owner) {
        // Direct app object
        app = result;
      }

      if (!app) {
        console.log(`[FluxNode] App spec for ${appName}: no app data found in response`);
        return null;
      }
      console.log(`[FluxNode] App spec for ${appName}: owner=${app.owner || 'none'}, name=${app.name || 'none'}`);

      // Parse compose array
      const compose: FluxAppComponent[] = [];
      if (app.compose && Array.isArray(app.compose)) {
        for (const comp of app.compose) {
          compose.push({
            name: comp.name || '',
            description: comp.description || '',
            repotag: comp.repotag || '',
            owner: comp.owner || '',
            ports: comp.ports || [],
            domains: comp.domains || [],
            environmentParameters: comp.environmentParameters || comp.enviromentParameters || [],
            commands: comp.commands || [],
            containerPorts: comp.containerPorts || [],
            containerData: comp.containerData || '',
            cpu: comp.cpu || 0,
            ram: comp.ram || 0,
            hdd: comp.hdd || 0,
            tiered: comp.tiered || false
          });
        }
      }

      return {
        name: app.name || '',
        description: app.description || '',
        owner: app.owner || '',
        hash: app.hash || '',
        height: app.height || 0,
        version: app.version || 1,
        instances: app.instances || 0,
        compose,
        contacts: app.contacts || [],
        geolocation: app.geolocation || [],
        expire: app.expire || 0,
        nodes: app.nodes || []
      };
    } catch (error) {
      console.error('[FluxNode] Error fetching app specification:', error);
      return null;
    }
  }

  /**
   * Calculate app cost estimation based on resources
   * FLUX pricing (approximate): CPU cores * 0.5 + RAM GB * 0.1 + HDD GB * 0.01 per month
   */
  calculateAppCost(cpu: number, ram: number, hdd: number, instances: number = 1): {
    monthlyCost: number;
    yearlyCost: number;
  } {
    // Flux Cloud pricing is approximately:
    // - CPU: ~0.5 FLUX per core per month
    // - RAM: ~0.1 FLUX per 100MB per month
    // - HDD: ~0.01 FLUX per GB per month
    const cpuCost = cpu * 0.5;
    const ramCost = (ram / 100) * 0.1;
    const hddCost = hdd * 0.01;

    const monthlyPerInstance = cpuCost + ramCost + hddCost;
    const monthlyCost = monthlyPerInstance * instances;
    const yearlyCost = monthlyCost * 12;

    return { monthlyCost, yearlyCost };
  }

  /**
   * Find wallet owners with deployed apps
   * Searches global app specs and returns owners with their app details
   * @param searchTerm Optional filter for app names (e.g., "wordpress", "presearch")
   * @param limit Max number of owners to return (default 30)
   */
  async findAppOwners(searchTerm: string = '', limit: number = 30): Promise<{
    owner: string;
    apps: { name: string; instances: number; description: string }[];
  }[]> {
    try {
      console.log('[FluxNode] Finding app owners...');

      // Get list of all registered apps (includes owner info)
      const allApps = await this.getRunningApps();
      console.log(`[FluxNode] Found ${allApps.length} registered apps`);

      // Filter by search term if provided
      let filteredApps = allApps;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredApps = allApps.filter(app =>
          app.name.toLowerCase().includes(term) ||
          app.description.toLowerCase().includes(term)
        );
        console.log(`[FluxNode] Filtered to ${filteredApps.length} apps matching "${searchTerm}"`);
      }

      // Group by owner - owner info is already in the app data
      const ownersMap = new Map<string, { name: string; instances: number; description: string }[]>();

      for (const app of filteredApps) {
        if (app.owner) {
          if (!ownersMap.has(app.owner)) {
            ownersMap.set(app.owner, []);
          }
          ownersMap.get(app.owner)!.push({
            name: app.name,
            instances: app.instances || 0,
            description: app.description || ''
          });
        }
      }

      // Convert to array and sort by number of apps
      const results = Array.from(ownersMap.entries())
        .map(([owner, apps]) => ({ owner, apps }))
        .sort((a, b) => b.apps.length - a.apps.length);

      console.log(`[FluxNode] Found ${results.length} unique owners`);

      return results;
    } catch (error) {
      console.error('[FluxNode] Error finding app owners:', error);
      return [];
    }
  }

  /**
   * Get reward payouts history from Explorer API
   * Fetches incoming transactions to the wallet address
   *
   * Uses the Insight API format with query parameters for better proxy compatibility
   */
  async getRewardPayouts(walletAddress: string, limit: number = 50): Promise<RewardPayout[]> {
    try {
      // Use query parameter format for better compatibility with proxy
      // Insight API supports: /txs?address={address}&pageNum={page}
      const url = this.buildUrl('explorer/txs', { address: walletAddress, pageNum: '0' });
      console.log('[FluxNode] Fetching reward payouts from:', url);

      const response = await fetch(url, this.fetchOptions);

      if (!response.ok) {
        console.error('[FluxNode] Failed to fetch transactions:', response.status, response.statusText);
        return [];
      }

      const data = await response.json();
      console.log('[FluxNode] Transaction response:', JSON.stringify(data).slice(0, 500));
      const payouts: RewardPayout[] = [];

      // Insight API /txs endpoint returns: { pagesTotal, txs: [...] }
      // Each tx has: { txid, vin, vout, time, blockheight, ... }
      const txs = data.txs || data.items || data.transactions || (Array.isArray(data) ? data : []);

      console.log('[FluxNode] Found', txs?.length || 0, 'transactions');

      if (Array.isArray(txs)) {
        for (const tx of txs.slice(0, limit)) {
          // Check each output (vout) to see if it goes to our wallet
          const vout = tx.vout || tx.outputs || [];
          let amountReceived = 0;

          for (const out of vout) {
            // Insight API format: { scriptPubKey: { addresses: [...] }, value: X }
            const addresses = out.scriptPubKey?.addresses ||
                             out.addresses ||
                             (out.address ? [out.address] : []);

            if (addresses && addresses.includes(walletAddress)) {
              // Value is in FLUX (not satoshis) in Insight API
              const value = typeof out.value === 'number'
                ? out.value
                : (typeof out.value === 'string' ? parseFloat(out.value) : 0);
              amountReceived += value;
            }
          }

          // Only add if we received something in this transaction
          if (amountReceived > 0) {
            // Insight API uses 'time' (unix timestamp in seconds)
            const timestampSeconds = tx.time || tx.blocktime || 0;
            const timestamp = timestampSeconds > 0 ? timestampSeconds * 1000 : Date.now();

            payouts.push({
              txid: tx.txid || '',
              timestamp,
              amount: amountReceived,
              blockHeight: tx.blockheight || 0
            });
          }
        }
      }

      console.log('[FluxNode] Parsed payouts:', payouts.length, 'transactions with incoming FLUX');
      return payouts;
    } catch (error) {
      console.error('[FluxNode] Error fetching reward payouts:', error);
      return [];
    }
  }

  /**
   * Get total rewards received for a wallet
   */
  async getTotalRewardsReceived(walletAddress: string): Promise<{
    total: number;
    last30Days: number;
    last7Days: number;
    payouts: RewardPayout[];
  }> {
    try {
      const payouts = await this.getRewardPayouts(walletAddress, 100);

      const now = Date.now();
      const day7 = now - 7 * 24 * 60 * 60 * 1000;
      const day30 = now - 30 * 24 * 60 * 60 * 1000;

      let total = 0;
      let last7Days = 0;
      let last30Days = 0;

      for (const payout of payouts) {
        total += payout.amount;
        if (payout.timestamp >= day7) {
          last7Days += payout.amount;
        }
        if (payout.timestamp >= day30) {
          last30Days += payout.amount;
        }
      }

      return { total, last30Days, last7Days, payouts };
    } catch (error) {
      console.error('Error calculating total rewards:', error);
      return { total: 0, last30Days: 0, last7Days: 0, payouts: [] };
    }
  }

  /**
   * Get estimated time until next reward for a node
   * Based on rank in the payment queue
   */
  estimateNextReward(rank: number): {
    blocks: number;
    seconds: number;
    formatted: string;
  } {
    const blocks = rank;
    const seconds = blocks * this.BLOCK_TIME_SECONDS;

    // Format the time
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    let formatted = '';
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      formatted = `${days}j ${remainingHours}h`;
    } else if (hours > 0) {
      formatted = `${hours}h ${minutes}m`;
    } else {
      formatted = `${minutes}m`;
    }

    return { blocks, seconds, formatted };
  }
}

// Export singleton instance
export const fluxNodeAPI = new FluxNodeAPIService();
